/**
 * Mirrors the selected Fly Bundles percentage tier as a per-unit product price.
 * Fly owns selection and checkout pricing; this element is display-only.
 */
(function () {
  'use strict';

  if (customElements.get('zp-fly-bundle-price')) return;

  class ZpFlyBundlePrice extends HTMLElement {
    connectedCallback() {
      this.onInteraction = this.onInteraction.bind(this);
      this.onVariantUpdate = this.onVariantUpdate.bind(this);
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.observeFlyWidget = this.observeFlyWidget.bind(this);

      document.addEventListener('change', this.onInteraction, true);
      document.addEventListener('click', this.onInteraction, true);
      this.section = this.closest('.shopify-section');
      this.section?.addEventListener('variant:update', this.onVariantUpdate);

      this.observer = new MutationObserver(() => {
        this.observeFlyWidget();
        this.scheduleUpdate();
      });
      this.observeFlyWidget();
      this.scheduleUpdate();
      this.delayedUpdate = window.setTimeout(this.scheduleUpdate, 1200);
      this.observerTimeout = window.setTimeout(() => {
        if (!this.getFlyRoot()) this.observer?.disconnect();
      }, 10000);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onInteraction, true);
      document.removeEventListener('click', this.onInteraction, true);
      this.section?.removeEventListener('variant:update', this.onVariantUpdate);
      this.observer?.disconnect();
      this.observerTarget = null;
      this.section = null;
      window.clearTimeout(this.delayedUpdate);
      window.clearTimeout(this.observerTimeout);
      window.cancelAnimationFrame(this.updateFrame);
    }

    onInteraction(event) {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.gfbVolumeBundleWrapper, .gfbVolumeBlock')) return;
      this.observeFlyWidget();
      this.scheduleUpdate();
      window.setTimeout(this.scheduleUpdate, 50);
    }

    onVariantUpdate(event) {
      const detail = event.detail || {};
      if (String(detail.data?.productId || '') !== String(this.dataset.productId || '')) return;
      const price = Number(detail.resource?.price);
      if (Number.isFinite(price) && price >= 0) this.dataset.basePrice = String(price);
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      window.cancelAnimationFrame(this.updateFrame);
      this.updateFrame = window.requestAnimationFrame(() => this.updatePrice());
    }

    getFlyRoot() {
      return document.querySelector(
        '#MainContent .gfbVolumeBundleWrapper, #MainContent .gfbVolumeBlock'
      );
    }

    observeFlyWidget() {
      const target = this.getFlyRoot() || document.querySelector('#MainContent');
      if (!target || target === this.observerTarget) return;
      this.observer.disconnect();
      this.observer.observe(target, { childList: true, subtree: true });
      if (target !== document.querySelector('#MainContent') && target.parentElement) {
        this.observer.observe(target.parentElement, { childList: true });
      }
      this.observerTarget = target;
    }

    getSelection() {
      const root = this.getFlyRoot();
      if (!root) return { discount: '0', type: 'NO_DISCOUNT' };

      const radios = [...root.querySelectorAll('input[type="radio"]')];
      const checkedIndex = radios.findIndex(
        (radio) => radio instanceof HTMLInputElement && radio.checked
      );
      const selectedRadio = radios[checkedIndex] || radios[0];
      const radioOfferId = selectedRadio?.name?.replace(/^tier-/, '');
      const offers = window.fly_app_data?.data?.volumeBundleData;
      const offer = Array.isArray(offers)
        ? offers.find((candidate) => candidate?.offerId === radioOfferId) || offers[0]
        : null;
      const rules = Array.isArray(offer?.rules) ? offer.rules : [];
      if (rules.length === 0) return { discount: '0', type: 'NO_DISCOUNT' };

      const selectedQuantity = this.positiveNumber(selectedRadio?.dataset?.tierCount);
      let rule = selectedQuantity == null
        ? null
        : rules.find((candidate) => this.positiveNumber(candidate?.conditionValue) === selectedQuantity);

      if (!rule) {
        const activeTierIndex = window.gfb?.state?.activeTierIndex;
        const tierIndex = typeof activeTierIndex === 'number' &&
          Number.isInteger(activeTierIndex) &&
          activeTierIndex >= 0 &&
          activeTierIndex < rules.length
          ? activeTierIndex
          : checkedIndex;
        rule = rules[tierIndex] || rules[0] || {};
      }

      return {
        discount: String(rule.discountValue ?? '0'),
        type: String(offer?.discountType || 'NO_DISCOUNT').toUpperCase(),
      };
    }

    positiveNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) && number > 0 ? number : null;
    }

    discountedUnitPrice(basePrice, selection) {
      if (selection.type === 'PERCENTAGE') {
        const parsed = this.decimalRatio(selection.discount);
        if (!parsed) return basePrice;
        const denominator = 100 * parsed.scale;
        const discountUnits = Math.min(denominator, parsed.units);
        const numerator = basePrice * (denominator - discountUnits);
        return Math.max(0, Math.floor((numerator + denominator / 2) / denominator));
      }
      return basePrice;
    }

    decimalRatio(value) {
      const match = String(value).trim().match(/^(\d+)(?:\.(\d{1,6}))?$/);
      if (!match) return null;
      const decimals = match[2] || '';
      const scale = 10 ** decimals.length;
      const units = Number(match[1]) * scale + Number(decimals || 0);
      return Number.isSafeInteger(units) ? { units, scale } : null;
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (_) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    setText(element, value) {
      if (element && element.textContent !== value) element.textContent = value;
    }

    updatePrice() {
      const basePrice = Math.max(0, Number(this.dataset.basePrice) || 0);
      const selection = this.getSelection();
      const unitPrice = this.discountedUnitPrice(basePrice, selection);
      const unitElement = this.querySelector('[data-zp-fly-unit]');
      const compareElement = this.querySelector('[data-zp-fly-compare]');

      this.setText(unitElement, this.formatMoney(unitPrice));

      if (compareElement) {
        const showCompare = this.dataset.showCompare === 'true' && unitPrice < basePrice;
        compareElement.hidden = !showCompare;
        this.setText(compareElement, showCompare ? this.formatMoney(basePrice) : '');
      }
    }
  }

  customElements.define('zp-fly-bundle-price', ZpFlyBundlePrice);
})();
